import React, { useState, useEffect, useCallback } from 'react';
import Input from '../../../components/ui/Input';
import Select from '../../../components/ui/Select';
import Button from '../../../components/ui/Button';
import { Checkbox } from '../../../components/ui/Checkbox';


const TournamentSetupTab = ({ onSave, initialData = null, isEditMode = false }) => {
  const [formData, setFormData] = useState(initialData || {
    name: '',
    format: '',
    matchType: '',
    startDate: '',
    endDate: '',
    description: '',
    maxParticipants: '',
    status: 'upcoming',
    prize1: '',
    prize2: '',
    prize3: '',
    enableConfidenceSlider: true
  });

  const [errors, setErrors] = useState({});
  const [isSaving, setIsSaving] = useState(false);

  // Update form data when initialData changes
  React.useEffect(() => {
    if (initialData) {
      setFormData({
        name: initialData?.name || '',
        format: initialData?.format || '',
        matchType: initialData?.matchType || '',
        startDate: initialData?.startDate ? new Date(initialData.startDate)?.toISOString()?.split('T')?.[0] : '',
        endDate: initialData?.endDate ? new Date(initialData.endDate)?.toISOString()?.split('T')?.[0] : '',
        description: initialData?.description || '',
        maxParticipants: initialData?.maxParticipants?.toString() || '',
        status: initialData?.status || 'upcoming',
        prize1: initialData?.prize1 || '',
        prize2: initialData?.prize2 || '',
        prize3: initialData?.prize3 || '',
        enableConfidenceSlider: initialData?.enableConfidenceSlider !== undefined ? initialData?.enableConfidenceSlider : true
      });
    }
  }, [initialData]);

  const formatOptions = [
    { value: 'T20', label: 'T20 International' },
    { value: 'ODI', label: 'One Day International' },
    { value: 'Test', label: 'Test Match' }
  ];

  const matchTypeOptions = [
    { value: 'International', label: 'International' },
    { value: 'Domestic', label: 'Domestic' },
    { value: 'League', label: 'League' },
    { value: 'Franchise', label: 'Franchise' }
  ];

  const currencyOptions = [
    { value: 'USD', label: 'US Dollar ($)' },
    { value: 'EUR', label: 'Euro (€)' },
    { value: 'GBP', label: 'British Pound (£)' },
    { value: 'INR', label: 'Indian Rupee (₹)' }
  ];

  const statusOptions = [
    { value: 'upcoming', label: 'Upcoming' },
    { value: 'active', label: 'Active' },
    { value: 'completed', label: 'Completed' },
    { value: 'cancelled', label: 'Cancelled' }
  ];

  const handleInputChange = useCallback((field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    if (errors?.[field]) {
      setErrors(prev => ({ ...prev, [field]: '' }));
    }
  }, [errors]);

  const validateForm = () => {
    const newErrors = {};

    if (!formData?.name?.trim()) {
      newErrors.name = 'Tournament name is required';
    }

    if (!formData?.format) {
      newErrors.format = 'Tournament format is required';
    }

    if (!formData?.matchType) {
      newErrors.matchType = 'Match type is required';
    }

    if (!formData?.startDate) {
      newErrors.startDate = 'Start date is required';
    }

    if (!formData?.endDate) {
      newErrors.endDate = 'End date is required';
    }

    if (formData?.startDate && formData?.endDate && new Date(formData.startDate) >= new Date(formData.endDate)) {
      newErrors.endDate = 'End date must be after start date';
    }

    if (formData?.maxParticipants && (isNaN(parseInt(formData?.maxParticipants)) || parseInt(formData?.maxParticipants) < 1)) {
      newErrors.maxParticipants = 'Max participants must be a positive number';
    }

    setErrors(newErrors);
    return Object.keys(newErrors)?.length === 0;
  };

  const handleSave = async () => {
    if (!validateForm()) {
      return;
    }

    setIsSaving(true);
    try {
      await new Promise(resolve => setTimeout(resolve, 1000));
      
      // Convert form data to database format
      const dataToSave = {
        name: formData?.name,
        format: formData?.format,
        matchType: formData?.matchType,
        startDate: formData?.startDate,
        endDate: formData?.endDate,
        description: formData?.description || null,
        maxParticipants: formData?.maxParticipants?.trim() === '' ? null : parseInt(formData?.maxParticipants),
        status: formData?.status,
        prize1: formData?.prize1 || null,
        prize2: formData?.prize2 || null,
        prize3: formData?.prize3 || null,
        enableConfidenceSlider: formData?.enableConfidenceSlider
      };
      
      onSave(dataToSave);
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <div className="space-y-6 md:space-y-8">
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 md:gap-6">
        <Input
          label="Tournament Name"
          type="text"
          placeholder="e.g., ICC World Cup 2026"
          value={formData?.name}
          onChange={(e) => handleInputChange('name', e?.target?.value)}
          error={errors?.name}
          required
        />

        <Select
          label="Tournament Format"
          placeholder="Select format"
          options={formatOptions}
          value={formData?.format}
          onChange={(value) => handleInputChange('format', value)}
          error={errors?.format}
          required
        />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 md:gap-6">
        <Select
          label="Match Type"
          placeholder="Select match type"
          options={matchTypeOptions}
          value={formData?.matchType}
          onChange={(value) => handleInputChange('matchType', value)}
          error={errors?.matchType}
          required
        />

        <Select
          label="Tournament Status"
          options={statusOptions}
          value={formData?.status}
          onChange={(value) => handleInputChange('status', value)}
          description="Set tournament visibility and state"
        />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 md:gap-6">
        <Input
          label="Start Date"
          type="date"
          value={formData?.startDate}
          onChange={(e) => handleInputChange('startDate', e?.target?.value)}
          error={errors?.startDate}
          required
        />

        <Input
          label="End Date"
          type="date"
          value={formData?.endDate}
          onChange={(e) => handleInputChange('endDate', e?.target?.value)}
          error={errors?.endDate}
          required
        />
      </div>

      <Input
        label="Tournament Description"
        type="text"
        placeholder="Brief description of the tournament"
        value={formData?.description}
        onChange={(e) => handleInputChange('description', e?.target?.value)}
        description="Optional: Provide additional context about the tournament"
      />

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 md:gap-6">
        <Input
          label="Max Participants"
          type="number"
          placeholder="100"
          value={formData?.maxParticipants}
          onChange={(e) => handleInputChange('maxParticipants', e?.target?.value)}
          error={errors?.maxParticipants}
          description="Leave empty for unlimited participants"
        />
      </div>

      <div className="space-y-4">
        <h3 className="text-lg font-semibold text-foreground">Prediction Settings</h3>
        <Checkbox
          id="enableConfidenceSlider"
          checked={formData?.enableConfidenceSlider}
          onChange={(e) => handleInputChange('enableConfidenceSlider', e?.target?.checked)}
          label="Enable Confidence Level Sliders"
          description="Allow users to set confidence levels for their predictions. When enabled, users can adjust confidence multipliers that affect their points."
        />
      </div>

      <div className="space-y-4">
        <h3 className="text-lg font-semibold text-foreground">Prize Declaration</h3>
        <p className="text-sm text-muted-foreground">Declare non-cash prizes for top 3 positions (e.g., iPad, Gift Coupons, etc.)</p>
        <div className="grid grid-cols-1 gap-4">
          <Input
            label="1st Place Prize"
            type="text"
            placeholder="e.g., iPad Pro 12.9-inch"
            value={formData?.prize1}
            onChange={(e) => handleInputChange('prize1', e?.target?.value)}
            description="Prize for the tournament winner"
          />
          <Input
            label="2nd Place Prize"
            type="text"
            placeholder="e.g., $500 Amazon Gift Card"
            value={formData?.prize2}
            onChange={(e) => handleInputChange('prize2', e?.target?.value)}
            description="Prize for the runner-up"
          />
          <Input
            label="3rd Place Prize"
            type="text"
            placeholder="e.g., Wireless Headphones"
            value={formData?.prize3}
            onChange={(e) => handleInputChange('prize3', e?.target?.value)}
            description="Prize for the third position"
          />
        </div>
      </div>

      <div className="flex flex-col sm:flex-row gap-3 pt-4 border-t border-border">
        <Button
          variant="default"
          onClick={handleSave}
          loading={isSaving}
          iconName="Save"
          iconPosition="left"
          className="sm:w-auto"
        >
          Save Tournament
        </Button>
        <Button
          variant="outline"
          onClick={() => setFormData(initialData || {})}
          disabled={isSaving}
          iconName="RotateCcw"
          iconPosition="left"
          className="sm:w-auto"
        >
          Reset Form
        </Button>
      </div>
    </div>
  );
};

export default TournamentSetupTab;